import Model from 'src/app/common/model';
import FarmSiteModel from './farmSite.model';
import PersonsModel from './persons.model';

/**
 * @model BusinessEntity
 * @description
 * Represents a Business Entity within the AquaCloud ecosystem.
 * A Business Entity is the primary organizational unit, representing real-world entities
 * such as Farms, Hatcheries, Feed Providers, Processing Plants, or Laboratories.
 *
 * **Purpose:**
 * This model acts as the central node for organization-related data. It aggregates
 * physical locations (`FarmSiteModel`), personnel (`PersonsModel`), IoT infrastructure
 * (`CustomerDeviceModel`), and business classification (`BusinessType`, `BusinessCategory`).
 *
 * **Key Responsibilities:**
 * 1. **Identity Management**: Stores core business details like `businessName`, `mobileNumber`, and branding.
 * 2. **Regulatory Compliance**: Tracks government-issued identifiers like `CAA_ID` (Coastal Aquaculture Authority) and `MPEDA_ID`.
 * 3. **Hierarchy & Relationships**:
 *    - Links to `FarmSites` (where operations happen).
 *    - Links to `Persons` (who manage operations).
 *    - Links to `CustomerDevices` (IoT hardware owned by the business).
 * 4. **Classification**: Defines the nature of the business via `businessType` and `businessCategory` to determine available features.
 *
 * **Data Flow:**
 * - **Registration**: Created during the onboarding process.
 * - **Management**: Updated by Business Admins to reflect changes in address, contact info, or operational status.
 * - **Consumption**: Used throughout the application to scope data access (e.g., a user sees data only for their Business Entity).
 *
 * **User Roles & Permissions:**
 * - **System Admin**: Can create/verify business entities.
 * - **Business Owner/Admin**: Can update profile details and manage sub-entities (sites, users).
 * - **Employees**: Associated with the entity to perform daily tasks.
 *
 * **Features:**
 * - Supports multiple addresses.
 * - Handles branding attachments (logos).
 * - Tracks operational history (`yearStarted`, `numberOfCulturesDone`).
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class BusinessEntity extends Model {

  /**
   * @type {Array<FarmSiteModel>}
   * @description Collection of farm sites (physical locations) owned or managed by this business.
   * @optional
   */
  FarmSiteBusinessEntity: Array<FarmSiteModel>;

  /**
   * @type {Array<PersonsModel>}
   * @description List of persons (employees, owners) associated with this business.
   * Note: 'person' and 'Persons' may be used interchangeably depending on API version.
   * @optional
   */
  person: Array<PersonsModel>;

  /**
   * @type {Array<PersonsModel>}
   * @description Alternative property for list of associated persons.
   * @optional
   */
  Persons: Array<PersonsModel>;

  /**
   * @type {Array<BrandingAttachments>}
   * @description Collection of branding assets like logos or banners.
   * @optional
   */
  branding_attachments: Array<BrandingAttachments>;

  /**
   * @type {Array<Address>}
   * @description List of physical addresses associated with the business (HQ, billing, etc.).
   * @optional
   */
  address: Array<Address>;

  /**
   * @type {Array<BusinessType>}
   * @description Classification of the business type (e.g., Farm, Provider).
   * @optional
   */
  businessType: Array<BusinessType>;

  /**
   * @type {Array<BusinessCategory>}
   * @description Categorization details (e.g., Shrimp Farm, Fish Farm).
   * @optional
   */
  businessCategory: Array<BusinessCategory>;

  /**
   * @type {Array<ProviderID>}
   * @description References to parent providers or associated service providers.
   * @optional
   */
  providerID: Array<ProviderID>;

  /**
   * @type {string}
   * @description The legal or display name of the business.
   */
  businessName: string;

  /**
   * @type {string}
   * @description Landline contact number.
   * @optional
   */
  landlineNumber?: string;

  /**
   * @type {string}
   * @description Primary mobile contact number.
   */
  mobileNumber: string;

  /**
   * @type {string}
   * @description Official email address for the business.
   * @optional
   */
  email?: string;

  /**
   * @type {Date | string}
   * @description The date when the business entity was registered or started operations.
   * @optional
   */
  startDate?: Date | string;

  /**
   * @type {Date | string}
   * @description The date when the business ceased operations (if applicable).
   * @optional
   */
  endDate?: Date | string;

  /**
   * @type {string}
   * @description The year the business was established.
   * @optional
   */
  yearStarted?: string;

  /**
   * @type {number}
   * @description Total number of culture cycles completed by this business.
   */
  numberOfCulturesDone: number;

  /**
   * @type {boolean}
   * @description Flag indicating if the business is currently active in the system.
   */
  isActive: boolean;

  /**
   * @type {any}
   * @description Current status object or code (e.g., Verified, Pending).
   */
  status: any;

  /**
   * @type {boolean}
   * @description Flag indicating if custom branding is enabled for this business.
   */
  branding: boolean;

  /**
   * @type {string}
   * @description ID of the primary contact person or owner.
   */
  personId: string;

  /**
   * @type {string}
   * @description Coastal Aquaculture Authority (CAA) Registration ID.
   * Regulatory requirement for aquaculture farms in certain regions.
   */
  CAA_ID: string;

  /**
   * @type {string}
   * @description Marine Products Export Development Authority (MPEDA) ID.
   * Regulatory requirement for export-oriented units.
   */
  MPEDA_ID: string;

  /**
   * @type {string}
   * @description Validity date string for the registration/subscription.
   */
  validThrough: string;

  /**
   * @type {string}
   * @description ID of the feed provider associated with this farm (if applicable).
   */
  feedProviderID: string;

  /**
   * @type {number}
   * @description ID representing the category of culture practiced.
   */
  cultureCategoryID: number;

  /**
   * @type {any[]}
   * @description Generic array for storing result sets or temporary data.
   */
  result: any[];

  /**
   * @type {string}
   * @description Display name alias.
   */
  name: string;

  /**
   * @type {string}
   * @description International dialing code for the contact numbers.
   */
  dialingCode: string;

  /**
   * @type {string}
   * @description Linked account identifier for billing/subscription.
   * @optional
   */
  accountID?: string;

  /**
   * @type {string}
   * @description Status of the linked account (e.g., 'Active', 'Suspended').
   * @optional
   */
  accountStatus?: string;

  /**
   * @type {string}
   * @description Unique system-generated code for the business.
   */
  businessCode?: string;

  agreedBCPolicyTerms?: boolean;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the BusinessEntity model.
   * Sets up empty arrays for nested collections (`branding_attachments`, `businessType`,
   * `businessCategory`, `providerID`, `address`, `FarmSiteBusinessEntity`) to ensure
   * they are iterable immediately upon instantiation.
   */
  constructor() {
    super();
    this.branding_attachments = new Array();
    this.businessType = new Array();
    this.businessCategory = new Array();
    this.providerID = new Array();
    this.address = new Array();
    this.FarmSiteBusinessEntity = new Array();
  }
}

//////////////////////////////
//Address
//////////////////////////////
/**
 * @model Address
 * @description
 * Represents a physical address structure used by Business Entities and Farm Sites.
 * Includes standard postal fields and geolocation context.
 */
export class Address {
  /** @type {string} @description Unique identifier for the address record. @optional */
  _id?: string;
  /** @type {string} @description House or building number. */
  houseNo: string;
  /** @type {string} @description Street name or number. */
  street: string;
  /** @type {string} @description Nearby landmark for location identification. */
  landMark: string;
  /** @type {string} @description State or Province name. */
  state: string;
  /** @type {string} @description District or County name. */
  district: string;
  /** @type {string} @description City or Town name. */
  city: string;
  /** @type {string} @description Postal/ZIP code. */
  pincode: string;
  /** @type {string} @description Country name. */
  country: string;
  /** @type {string} @description ISO country code (e.g., 'IN', 'US'). */
  countryCode: string;

  /** @constructor @description Initializes a new Address instance. */
  constructor() { }
}

//////////////////////////////
//BrandingAttachments
//////////////////////////////
/**
 * @model BrandingAttachments
 * @description
 * Represents file attachments specifically used for business branding,
 * such as logos, banners, or certification images.
 */
export class BrandingAttachments {
  /** @type {string} @description URL path to the stored file. @optional */
  file_path?: string;
  /** @type {string} @description MIME type or file extension (e.g., 'image/png'). */
  file_type: string;
  /** @type {string} @description Base64 encoded data string (used during upload). @optional */
  data?: string;

  /** @constructor @description Initializes a new BrandingAttachments instance. */
  constructor() { }
}

//////////////////////////////
//BusinessType
//////////////////////////////
/**
 * @model BusinessType
 * @description
 * Lightweight reference object for Business Type.
 * Used within the BusinessEntity to link to a specific business classification.
 */
export class BusinessType {
  /** @type {string} @description Unique identifier for the business type. @optional */
  businessTypeID?: string;
  /** @type {string} @description Display name of the business type. */
  name: string;

  /** @constructor @description Initializes a new BusinessType instance. */
  constructor() { }
}

//////////////////////////////
//BusinessCategory
//////////////////////////////
/**
 * @model BusinessCategory
 * @description
 * Lightweight reference object for Business Category.
 * Used within the BusinessEntity to link to a specific category (e.g., Shrimp vs Fish).
 */
export class BusinessCategory {
  /** @type {string} @description Unique identifier for the business category. @optional */
  businessCategoryID?: string;
  /** @type {string} @description Display name of the category. */
  name: string;

  /** @constructor @description Initializes a new BusinessCategory instance. */
  constructor() { }
}

//////////////////////////////
//ProviderID
//////////////////////////////
/**
 * @model ProviderID
 * @description
 * Wrapper class for storing associated Provider Business IDs.
 * Used when a business is linked to a service provider (e.g., a Farm linked to a Feed Provider).
 */
export class ProviderID {
  /** @type {string} @description The ID of the associated provider business. @optional */
  businessID?: string;

  /** @constructor @description Initializes a new ProviderID instance. */
  constructor() { }
}

/**
 * @type {BusinessTypeName}
 * @description
 * Union type defining the allowed string literals for Business Type names.
 * Used for type safety when checking business roles.
 */
export type BusinessTypeName = 'Farm Provider' | 'Farm' | 'AFS' | 'Internal';
